import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Web } from '@mui/icons-material';
import { Form } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';

import { arAgingDetailColumns } from './ARAgingReportDetailColumns';
import ARAgingReportTable, { arAgingColumns } from './ARAgingReportTable';
import ARAgingReportTableHistorical, {
  arAgingColumns as arAgingColumnsHistorical,
} from './ARAgingReportTableHistorical';
import { ARAgingReportForm } from './Form';
import ExcelBlack from '../../../assets/excel-black.png';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, ReportLoader, SubHeader } from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import { generateReport, getReportData, removeReportData, getList } from '../operations';

import './styles.scoped.css';

const ARAgingForm = ({
  form,
  setForm,
  AMCCodeOptions,
  branchNumberOptions,
  LOBCodeOptions,
  isLoadingAMCCodes,
  isLoadingBranchNumbers,
  isLoadingLOBCodes,
  handleViewResults,
}) => {
  const navigate = useNavigate();
  const handleChange = e => {
    const _form = {
      ...form,
      [e.target.name]:
        e.target.name.includes('AgingDays') || e.target.name.includes('BillToNumber')
          ? Number(e.target.value)
          : e.target.value,
    };
    setForm(_form);
  };

  return (
    <Form
      noValidate
      onSubmit={() => {}}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <ARAgingReportForm
        form={form}
        setForm={setForm}
        handleChange={handleChange}
        AMCCodeOptions={AMCCodeOptions}
        branchNumberOptions={branchNumberOptions}
        LOBCodeOptions={LOBCodeOptions}
        isLoadingAMCCodes={isLoadingAMCCodes}
        isLoadingBranchNumbers={isLoadingBranchNumbers}
        isLoadingLOBCodes={isLoadingLOBCodes}
      />

      <div className="buttons-row">
        <Button variant="primary" onPress={() => handleViewResults()} style={{ marginRight: '20px', width: '100px' }} text="View" />

        <Button
          variant="outline"
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          text="Cancel"
        />
      </div>
    </Form>
  );
};

const ARAgingReport = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [AMCCodes, setAMCCodes] = useState([]);
  const [branchNumbers, setBranchNumbers] = useState([]);
  const [LOBCodes, setLOB] = useState([]);

  const [form, setForm] = useState({
    Historical: 0,
    SortSequence: 1,
    AgingDaysLessThan: 30,
    AgingDaysBetweenFrom1: 30,
    AgingDaysBetweenTo1: 60,
    AgingDaysBetweenFrom2: 60,
    AgingDaysBetweenTo2: 90,
    Branch: '',
    JobBranch: '',
    AMC: '',
    BillToNumber: 0,
  });

  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // JLOC Detail (CP-828): a second, on-demand export fetched only when its icon is clicked.
  // The visible black icon triggers the fetch; a hidden CSVLink performs the actual download
  // once the data arrives (there is no data at render time, so the icon can't wrap a live link).
  const [exportData, setExportData] = useState([]);
  const [loadingExport, setLoadingExport] = useState(false);
  const detailCsvRef = useRef(null);
  const shouldDownloadRef = useRef(false);

  const [isLoadingAMCCodes, setIsLoadingAMCCodes] = useState(false);
  const [isLoadingBranchNumbers, setIsLoadingBranchNumbers] = useState(false);
  const [isLoadingLOBCodes, setIsLoadingLOB] = useState(false);

  const handleGetAMCCodes = async () => {
    setIsLoadingAMCCodes(true);
    const amcc = await getList('amcs');
    setAMCCodes(amcc);
    setIsLoadingAMCCodes(false);
  };

  const handleGetBranchNumbers = async () => {
    setIsLoadingBranchNumbers(true);
    const bn = await getList('branches');
    setBranchNumbers(bn);
    setIsLoadingBranchNumbers(false);
  };

  const handleGetLOB = async () => {
    setIsLoadingLOB(true);
    const lob = await getList('line-of-business');
    setLOB(lob);
    setIsLoadingLOB(false);
  };

  useEffect(() => {
    handleGetAMCCodes();
    handleGetBranchNumbers();
    handleGetLOB();
  }, []);

  const AMCCodeOptions = useMemo(
    () =>
      AMCCodes.map(({ Code, Description }) => ({
        label: Description,
        value: Code,
      })),
    [AMCCodes],
  );

  const branchNumberOptions = useMemo(
    () =>
      branchNumbers.map(({ Branch, BranchName }) => ({
        label: BranchName,
        value: Branch,
      })),
    [branchNumbers],
  );

  const LOBCodeOptions = useMemo(
    () =>
      LOBCodes.map(({ LineOfBusinessCode, LineOfBusinessDesc }) => ({
        label: LineOfBusinessDesc,
        value: LineOfBusinessCode,
      })),
    [LOBCodes],
  );

  const fileName = `edge-reporting-ar-aging-report${
    form?.Historical ? '-historical' : ''
  }-${newDateFormatted.toString()}.csv`;

  const detailFileName = `edge-reporting-ar-aging-report-detail-${newDateFormatted.toString()}.csv`;

  // Once the on-demand fetch flushes exportData into state, fire the hidden CSVLink to download.
  useEffect(() => {
    if (shouldDownloadRef.current && exportData?.length) {
      detailCsvRef.current?.link?.click();
      shouldDownloadRef.current = false;
    }
  }, [exportData]);

  // JLOC Detail export — runs the heavier `aging-report-detail` proc through the same
  // long-running report pipeline as the summary, using the current criteria unchanged.
  const handleExportDetail = async () => {
    setLoadingExport(true);
    try {
      const response = await generateReport({
        endpoint: 'aging-report-detail',
        payload: form,
      });
      if (response?.statusCode === 200 && response?.location) {
        const reportData = await getReportData(response.location);
        await removeReportData(response.location);
        if (reportData?.length) {
          shouldDownloadRef.current = true;
          setExportData(reportData);
        } else {
          // eslint-disable-next-line no-undef
          alert('No data found matching your criteria.');
        }
      } else {
        // eslint-disable-next-line no-undef
        alert(response?.statusDescription || 'No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingExport(false);
    }
  };

  const handleViewResults = async () => {
    setLoadingResults(true);
    try {
      const response = await generateReport({
        endpoint: 'aging-report',
        payload: form,
      });
      if (response?.statusCode === 200 && response?.location) {
        const reportData = await getReportData(response.location);
        await removeReportData(response.location);
        if (reportData?.length) {
          setData(reportData);
          setShowResults(true);
        } else {
          // eslint-disable-next-line no-undef
          alert('No data found matching your criteria.');
        }
      } else {
        // eslint-disable-next-line no-undef
        alert(response?.statusDescription || 'No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingResults(false);
    }
  };

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your AR Aging Report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'AR Aging Report'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? `Viewing Report${form?.Historical ? ' (Historical)' : ''}` : 'AR Aging Report'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <div className="excel-container">
            {/* JLOC Detail — black icon + label to the left of the brand export (mirrors the
                Deduction Register "Garn." treatment). Fetches on click; hidden link downloads. */}
            <div className="jloc-detail-export">
              <button
                type="button"
                className="jloc-detail-button"
                aria-label="Export JLOC Detail"
                disabled={loadingExport}
                onClick={handleExportDetail}>
                <ImageColorChanger
                  src={ExcelBlack}
                  brandColor="#000000"
                  style={{ padding: '20px 0 0 0', cursor: 'pointer' }}
                  alt="export-jloc-detail-to-excel"
                />
              </button>
              <p className="jloc-detail-label">JLOC Detail</p>
              <CSVLink
                ref={detailCsvRef}
                className="jloc-detail-hidden"
                filename={detailFileName}
                data={getCSVData(exportData, arAgingDetailColumns)}
              />
            </div>
            <CSVLink
              filename={fileName}
              data={getCSVData(data, form?.Historical ? arAgingColumnsHistorical : arAgingColumns)}>
              <ImageColorChanger
                src={Excel}
                style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
                alt="export-to-excel"
              />
            </CSVLink>
          </div>
        )}
      </div>
      {(loadingResults || loadingExport) && (
        <ReportLoader
          didAbort={() => {
            setLoadingResults(false);
            setLoadingExport(false);
          }}
        />
      )}
      {showResults ? (
        <>
          {form?.Historical ? (
            <ARAgingReportTableHistorical data={data} loading={loadingResults} />
          ) : (
            <ARAgingReportTable data={data} loading={loadingResults} />
          )}
          <div className="buttons-row">
            <Button variant="primary" onPress={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />

            <Button
              variant="outline"
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <ARAgingForm
              form={form}
              setForm={setForm}
              AMCCodeOptions={AMCCodeOptions}
              branchNumberOptions={branchNumberOptions}
              LOBCodeOptions={LOBCodeOptions}
              isLoadingAMCCodes={isLoadingAMCCodes}
              isLoadingBranchNumbers={isLoadingBranchNumbers}
              isLoadingLOBCodes={isLoadingLOBCodes}
              handleViewResults={handleViewResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default ARAgingReport;
